<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="!isPhoneNumberValid || isLoading"
    loading-message="Canceling Auto Retry Ride..."
    @submit="cancelAutoRetryRide"
    @reset="resetForm"
  >
    <template #title>
      Cancel Auto Retry Ride
    </template>

    <template #content>
      <fieldset>
        <div
          v-if="!isFamilyDashboard"
          class="row align-items-center"
        >
          <label class="col-sm-3 control-label"> Phone Number* </label>
          <phone-number-input
            v-model="phoneNumber"
            class="col-sm-7"
            :page-id="pageId"
          />
        </div>
      </fieldset>
    </template>

    <template #submit-button>
      Cancel Auto Retry
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import util from '@/Utils/util';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import { rideService } from '@/services/ride';
import { callerService } from '@/services/caller';

const pageId = 'cancel-auto-retry-ride';

const { customerPhoneNumber } = useCustomer();
const { isFamilyDashboard, isPartnerDashboard } = useDashboard();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { partnerId } = usePartner();

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(phoneNumber.value));

const phoneNumber = ref('');
const isLoading = ref(false);

const cancelAutoRetryRide = async () => {
  try {
    const isCancelConfirm = await showConfirmPopupDialog({
      title: 'Are you sure to cancel in-progress auto retry?',
      confirmButtonText: 'Cancel',
    });

    if (!isCancelConfirm) {
      return;
    }

    isLoading.value = true;

    // if the page is in Partner dashboard, firstly we should check caller is in partnership
    if (isPartnerDashboard.value && partnerId.value) {
      await callerService.fetchCustomerInfo({
        phoneNumber: phoneNumber.value,
        partnerId: partnerId.value,
      });
    }

    await rideService.unmarkAutoRetryGettingEstimate(phoneNumber.value);

    showSuccessPopupDialog({
      text: `The auto-retry ride for ${phoneNumber.value} has been canceled.`,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

const resetForm = () => {
  phoneNumber.value = customerPhoneNumber.value || '';
};

watch(customerPhoneNumber, resetForm, {
  immediate: true,
});
</script>
